// Detail-Ansicht eines Belegs (Cloud-Shape, schlank):
// - linke Spalte: PDF-Viewer (Presigned-S3-URL, ADR-016)
// - rechte Spalte: Meta + Mandant + Aktionen
//
// Im Cloud-Modell macht die BMD-Aufnahme der Bot-Host (Epic F). Der
// Sachbearbeiter triagiert hier nur: Mandant zuordnen → bestätigen
// (gibt den Beleg für den Transport frei) bzw. verwerfen.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useAssignMandant,
  useBeleg,
  useBelegPdfUrl,
  useBestaetigen,
  useMandanten,
  useVerwerfen,
} from "@/api/hooks";
import type { BelegDetail as BelegDetailT } from "@/api/types";
import { MANDANT_SOURCE_LABEL } from "@/api/types";
import { Button } from "@/components/Button";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/PageHeader";
import { PdfViewer } from "@/components/PdfViewer";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/Toast";

import "./BelegDetail.css";

export function BelegDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const beleg = useBeleg(id);

  if (beleg.isLoading) return <Loader label="Beleg wird geladen…" />;
  if (beleg.isError)
    return (
      <div className="bmd-card bmd-detail-error">
        Beleg konnte nicht geladen werden: {beleg.error.message}
        <div style={{ marginTop: 12 }}>
          <Link to="/" className="bmd-mono">
            zurück zum Dashboard
          </Link>
        </div>
      </div>
    );
  if (!beleg.data) return null;

  return <DetailView beleg={beleg.data} />;
}

function DetailView({ beleg }: { beleg: BelegDetailT }) {
  const pdf = useBelegPdfUrl(beleg.id);

  return (
    <>
      <PageHeader
        title={beleg.original_filename}
        subtitle={`Eingegangen ${formatDateTime(beleg.created_at)}`}
        action={
          <Link to="/" className="bmd-mono bmd-subtle">
            ← Dashboard
          </Link>
        }
      />

      <div className="bmd-detail-grid">
        <section className="bmd-detail-pdf">
          {pdf.isLoading ? (
            <div className="bmd-card"><Loader label="PDF wird geladen…" /></div>
          ) : pdf.isError || !pdf.data ? (
            <div className="bmd-card bmd-detail-error">
              PDF konnte nicht geladen werden.
            </div>
          ) : (
            <PdfViewer
              fileUrl={pdf.data}
              downloadUrl={pdf.data}
              filename={beleg.original_filename}
            />
          )}
        </section>

        <aside className="bmd-detail-side">
          <MetaCard beleg={beleg} />
          <MandantCard beleg={beleg} />
          <ActionsCard beleg={beleg} />
        </aside>
      </div>
    </>
  );
}

// --- Meta-Card -----------------------------------------------------

function MetaCard({ beleg }: { beleg: BelegDetailT }) {
  return (
    <div className="bmd-card">
      <div className="bmd-detail-status-row">
        <StatusBadge status={beleg.status} size="md" />
      </div>
      <dl className="bmd-detail-dl">
        <Field label="Zuordnung" value={MANDANT_SOURCE_LABEL[beleg.mandant_source]} />
        <dt>Belegdatum</dt>
        <dd className={beleg.belegdatum ? "bmd-mono" : undefined}>
          {beleg.belegdatum ?? "—"}
        </dd>
        <Field
          label="Größe"
          value={`${(beleg.file_size_bytes / 1024).toFixed(0)} KB`}
          mono
        />
      </dl>
      {beleg.bemerkung && (
        <div className="bmd-detail-bemerkung">
          <div className="bmd-label">Bemerkung</div>
          <div>{beleg.bemerkung}</div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={mono ? "bmd-mono" : undefined}>{value}</dd>
    </>
  );
}

// --- Mandant-Card --------------------------------------------------

function MandantCard({ beleg }: { beleg: BelegDetailT }) {
  const toast = useToast();
  // Zuordnen geht nur vor der Freigabe (Cloud erlaubt es nur bei `eingegangen`).
  const editable = beleg.status === "eingegangen";
  const mandanten = useMandanten(true);
  const assign = useAssignMandant(beleg.id);

  const [selected, setSelected] = useState<string>(
    beleg.mandant ? beleg.mandant.id : ""
  );

  return (
    <div className="bmd-card">
      <div className="bmd-row bmd-row-between" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Mandant</h3>
      </div>

      {beleg.mandant ? (
        <div className="bmd-stack-2 bmd-detail-mandant-current">
          <div className="bmd-detail-mandant-name">{beleg.mandant.name}</div>
          <div className="bmd-mono bmd-subtle">
            Kundennr · {beleg.mandant.bmd_kundennr}
          </div>
        </div>
      ) : (
        <div className="bmd-detail-mandant-empty">
          Noch kein Mandant zugeordnet.
          {editable && " Bitte unten auswählen."}
        </div>
      )}

      {editable && (
        <div className="bmd-stack-3" style={{ marginTop: 16 }}>
          <label className="bmd-label" htmlFor="mandant-select">
            {beleg.mandant ? "Mandant ändern" : "Mandant auswählen"}
          </label>
          <select
            id="mandant-select"
            className="bmd-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={mandanten.isLoading}
          >
            <option value="">— bitte wählen —</option>
            {(mandanten.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · {m.bmd_kundennr}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            loading={assign.isPending}
            disabled={!selected}
            onClick={() =>
              assign.mutate(
                { mandant_id: selected || null },
                {
                  onSuccess: (b) =>
                    toast.success(
                      `Mandant ${b.mandant?.name ?? ""} zugeordnet.`
                    ),
                  onError: (err) =>
                    toast.error(`Zuordnung fehlgeschlagen: ${err.message}`),
                }
              )
            }
          >
            Mandant zuordnen
          </Button>
        </div>
      )}
    </div>
  );
}

// --- Aktions-Card --------------------------------------------------

function ActionsCard({ beleg }: { beleg: BelegDetailT }) {
  const toast = useToast();
  const bestaetigen = useBestaetigen(beleg.id);
  const verwerfen = useVerwerfen(beleg.id);

  const canBestaetigen = beleg.status === "eingegangen" && !!beleg.mandant;
  const canVerwerfen = ["eingegangen", "bestaetigt", "fehler"].includes(
    beleg.status
  );

  if (!canBestaetigen && !canVerwerfen) {
    return (
      <div className="bmd-card">
        <h3>Aktionen</h3>
        <p className="bmd-muted">
          {beleg.status === "bestaetigt" ||
          beleg.status === "bmd_vorbereitet" ||
          beleg.status === "bmd_uebernommen"
            ? "Der Beleg ist für die BMD-Aufnahme freigegeben — den Rest erledigt der Bot-Host."
            : "Keine Aktionen mehr nötig."}
        </p>
      </div>
    );
  }

  return (
    <div className="bmd-card">
      <h3>Aktionen</h3>

      <div className="bmd-stack-3">
        {canBestaetigen && (
          <Button
            variant="primary"
            loading={bestaetigen.isPending}
            onClick={() =>
              bestaetigen.mutate(undefined, {
                onSuccess: () =>
                  toast.success("Beleg bestätigt und für den Bot-Host freigegeben."),
                onError: (err) =>
                  toast.error(`Bestätigen fehlgeschlagen: ${err.message}`),
              })
            }
          >
            Bestätigen & freigeben
          </Button>
        )}

        {beleg.status === "eingegangen" && !beleg.mandant && (
          <p className="bmd-muted" style={{ marginTop: 0 }}>
            Wähle erst oben einen Mandanten — dann erscheint der Bestätigen-Button.
          </p>
        )}

        {canVerwerfen && (
          <Button
            variant="danger"
            loading={verwerfen.isPending}
            onClick={() => {
              if (!window.confirm("Beleg wirklich verwerfen?")) return;
              verwerfen.mutate(undefined, {
                onSuccess: () => toast.info("Beleg verworfen."),
                onError: (err) =>
                  toast.error(`Verwerfen fehlgeschlagen: ${err.message}`),
              });
            }}
          >
            Verwerfen
          </Button>
        )}
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-AT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
